import { useEffect, useMemo, useState } from 'react';
import API_BASE from '../../utils/api.js';

const PRESET_CATEGORIES = [
  'Seating',
  'Lighting',
  'Rugs',
  'Textiles',
  'Casegoods',
  'Art',
  'Accessories',
  'Outdoor',
  'Custom',
];

// The account's own categories for this type, then the presets it doesn't have.
export function buildCategoryOptions(categories, type) {
  const filtered = categories.filter((c) => {
    if (!c?.name) return false;
    const t = c.type || 'both';
    return t === 'both' || t === type;
  });

  const names = new Set(filtered.map((c) => c.name.toLowerCase()));
  const preset = PRESET_CATEGORIES.filter((name) => !names.has(name.toLowerCase()));

  return {
    custom: filtered.map((c) => c.name),
    preset,
  };
}

export function buildSalesCodeOptions(salesAccounts) {
  return Object.entries(salesAccounts || {})
    .map(([name, config]) => ({
      name,
      salesCode: String(config?.salesCode || '')
        .trim()
        .toUpperCase(),
    }))
    .filter((row) => row.salesCode)
    .sort((a, b) => a.name.localeCompare(b.name));
}

const normalizeQbVendor = (vendor) => {
  const displayName =
    vendor.DisplayName ||
    vendor.CompanyName ||
    [vendor.GivenName, vendor.FamilyName].filter(Boolean).join(' ') ||
    'Vendor';

  return {
    id: vendor.Id,
    displayName,
    companyName: vendor.CompanyName || '',
    firstName: vendor.GivenName || '',
    lastName: vendor.FamilyName || '',
    email: vendor.PrimaryEmailAddr?.Address || '',
    phone: vendor.PrimaryPhone?.FreeFormNumber || vendor.Mobile?.FreeFormNumber || '',
    billAddr: vendor.BillAddr || {},
  };
};

export function useProductFormData(type) {
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [salesAccounts, setSalesAccounts] = useState({});
  const [qbVendors, setQbVendors] = useState([]);
  const [qbConnected, setQbConnected] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingQbVendors, setLoadingQbVendors] = useState(false);

  const userId = typeof window !== 'undefined' ? localStorage.getItem('userId') : '';
  const token = typeof window !== 'undefined' ? localStorage.getItem('idToken') : '';

  const categoryOptions = useMemo(() => buildCategoryOptions(categories, type), [categories, type]);

  const salesCodeOptions = useMemo(() => buildSalesCodeOptions(salesAccounts), [salesAccounts]);

  useEffect(() => {
    async function loadCategories() {
      if (!userId) return;
      setLoadingCategories(true);
      try {
        const res = await fetch(`${API_BASE}/api/categories/${encodeURIComponent(userId)}`);
        const data = await res.json();
        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load categories', err);
      } finally {
        setLoadingCategories(false);
      }
    }

    async function loadVendors() {
      if (!userId) return;
      setLoadingVendors(true);
      try {
        const res = await fetch(`${API_BASE}/api/vendors/${encodeURIComponent(userId)}`);
        const data = await res.json();
        setVendors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load vendors', err);
      } finally {
        setLoadingVendors(false);
      }
    }

    async function loadQbVendors() {
      if (!token) return;
      setLoadingQbVendors(true);
      try {
        const res = await fetch(`${API_BASE}/api/quickbooks/vendors`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.status === 409) {
          setQbConnected(false);
          setQbVendors([]);
          return;
        }

        if (!res.ok) {
          setQbConnected(false);
          setQbVendors([]);
          return;
        }

        const data = await res.json();
        const normalized = Array.isArray(data) ? data.map(normalizeQbVendor) : [];
        setQbConnected(true);
        setQbVendors(normalized);
      } catch (err) {
        console.error('Failed to load QuickBooks vendors', err);
        setQbConnected(false);
      } finally {
        setLoadingQbVendors(false);
      }
    }

    async function loadSalesAccounts() {
      if (!userId) return;
      try {
        const res = await fetch(`${API_BASE}/api/settings/${encodeURIComponent(userId)}`);
        if (!res.ok) return;
        const data = await res.json();
        setSalesAccounts(
          data?.salesAccounts && typeof data.salesAccounts === 'object' ? data.salesAccounts : {}
        );
      } catch (err) {
        console.error('Failed to load sales accounts', err);
      }
    }

    loadCategories();
    loadVendors();
    loadQbVendors();
    loadSalesAccounts();
  }, [userId, token]);

  const addCategory = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || !userId) return;

    const exists = categories.some((c) => c.name?.toLowerCase() === trimmed.toLowerCase());
    if (exists) return;

    try {
      const res = await fetch(`${API_BASE}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, name: trimmed, type }),
      });

      if (!res.ok && res.status !== 409) return;

      setCategories((prev) =>
        [...prev, { name: trimmed, type }].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        )
      );
    } catch (err) {
      console.error('Failed to create category', err);
    }
  };

  const deleteCategory = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || !userId) return false;

    try {
      const res = await fetch(
        `${API_BASE}/api/categories?userId=${encodeURIComponent(userId)}&name=${encodeURIComponent(trimmed)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) return false;
      setCategories((prev) => prev.filter((c) => c.name !== trimmed));
      return true;
    } catch (err) {
      console.error('Failed to delete category', err);
      return false;
    }
  };

  const addVendor = async (vendorInput) => {
    if (!userId) return null;

    const rawName = typeof vendorInput === 'string' ? vendorInput : vendorInput?.name;
    const trimmedName = (rawName || '').trim();
    if (!trimmedName) return null;

    const inputEmail = typeof vendorInput === 'string' ? '' : vendorInput?.email || '';
    const inputPhone = typeof vendorInput === 'string' ? '' : vendorInput?.phone || '';
    const emailLower = inputEmail.trim().toLowerCase();
    const phoneDigits = inputPhone.replace(/\D/g, '');

    const existing = vendors.find((v) => {
      if ((v.name || '').toLowerCase() === trimmedName.toLowerCase()) return true;
      if (emailLower && (v.email || '').trim().toLowerCase() === emailLower) return true;
      if (phoneDigits.length >= 7 && (v.phone || '').replace(/\D/g, '') === phoneDigits) return true;
      return false;
    });
    if (existing) return { ...existing, __duplicate: true };

    try {
      const payload =
        typeof vendorInput === 'string'
          ? { userId, name: trimmedName }
          : {
              userId,
              ...vendorInput,
              name: trimmedName,
            };

      const res = await fetch(`${API_BASE}/api/vendors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) return null;

      const data = await res.json();

      if (data.duplicate && data.vendor) {
        const existingVendor = { ...data.vendor, __duplicate: true };
        setVendors((prev) =>
          prev.some((v) => v.vendorId === existingVendor.vendorId)
            ? prev
            : [...prev, existingVendor].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        );
        return existingVendor;
      }

      const created = {
        ...(typeof vendorInput === 'string' ? {} : vendorInput),
        vendorId: data.vendorId,
        name: trimmedName,
      };
      setVendors((prev) =>
        [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      );
      return created;
    } catch (err) {
      console.error('Failed to create vendor', err);
      return null;
    }
  };

  const ensureVendorFromQb = async (qbVendor) => {
    if (!qbVendor || !userId) return null;

    const existing =
      vendors.find((v) => v.quickbooksVendorId === qbVendor.id) ||
      vendors.find((v) => (v.name || '').toLowerCase() === qbVendor.displayName.toLowerCase());

    if (existing) return existing;

    return addVendor({
      name: qbVendor.displayName,
      companyName: qbVendor.companyName || null,
      firstName: qbVendor.firstName || null,
      lastName: qbVendor.lastName || null,
      email: qbVendor.email || null,
      phone: qbVendor.phone || null,
      streetAddress: qbVendor.billAddr?.Line1 || null,
      city: qbVendor.billAddr?.City || null,
      state: qbVendor.billAddr?.CountrySubDivisionCode || null,
      zip: qbVendor.billAddr?.PostalCode || null,
      quickbooksVendorId: qbVendor.id,
    });
  };

  const addSalesCode = async ({ name, salesCode }) => {
    if (!userId) return null;

    const trimmedName = String(name || '').trim();
    const trimmedCode = String(salesCode || '')
      .trim()
      .toUpperCase();
    if (!trimmedName || !trimmedCode) return null;

    const existingByName = salesAccounts?.[trimmedName];
    if (existingByName?.salesCode) {
      return {
        name: trimmedName,
        salesCode: String(existingByName.salesCode).trim().toUpperCase(),
      };
    }

    const existingByCode = Object.entries(salesAccounts || {}).find(
      ([, cfg]) =>
        String(cfg?.salesCode || '')
          .trim()
          .toUpperCase() === trimmedCode
    );
    if (existingByCode) {
      return {
        name: existingByCode[0],
        salesCode: String(existingByCode[1]?.salesCode || '')
          .trim()
          .toUpperCase(),
      };
    }

    const nextSalesAccounts = {
      ...(salesAccounts || {}),
      [trimmedName]: {
        salesCode: trimmedCode,
        costSales: `Cost Sales ${trimmedName}`,
        incomeSales: `Income Sales ${trimmedName}`,
        incomeMarkup: `Income Sales ${trimmedName}`,
      },
    };

    const res = await fetch(
      `${API_BASE}/api/settings/${encodeURIComponent(userId)}/sales-accounts`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ salesAccounts: nextSalesAccounts }),
      }
    );

    if (!res.ok) {
      let message = 'Failed to save sales code';
      try {
        const body = await res.json();
        message = body?.error || message;
      } catch {}
      throw new Error(message);
    }

    setSalesAccounts(nextSalesAccounts);
    return { name: trimmedName, salesCode: trimmedCode };
  };

  return {
    categoryOptions,
    addCategory,
    deleteCategory,
    addVendor,
    vendors,
    qbVendors,
    qbConnected,
    loadingCategories,
    loadingVendors,
    loadingQbVendors,
    ensureVendorFromQb,
    salesCodeOptions,
    addSalesCode,
  };
}
